/**
 * Cluster `/insights/errors` into one row per (stage, error_type), with a trend
 * verdict.
 *
 * The endpoint returns per-(day, stage, error_type) buckets off `v_error_buckets`.
 * A raw listing does not answer the question that matters, which is not "what
 * errors happened" but **"what is new, and what is getting worse"** — the
 * 2026-08-26 null-list incident poisoned 141 events over three days at a rate
 * that never moved the aggregate failure percentage, and was found only by
 * hand-querying Postgres.
 *
 * So each cluster carries a `trend`, computed against the *last day present in
 * the payload* rather than wall-clock now: the insights views are matview-backed
 * and day-bucketed, so anchoring on the data keeps the verdict stable (and the
 * unit tests honest) regardless of when the page is opened.
 *
 * ## Self-healed errors are left out by default
 *
 * Most rows used to be errors a retry fixed moments later: over the seven days
 * to 2026-09-23, 1,397 of 1,948 (72%), all 971 `performance_dedup`
 * `AdminApiClientError`s among them. Tina marks those buckets `recovered`
 * (migration `0077`), and they are skipped unless `includeRecovered` is set, so
 * the trend verdict and the counts describe only what is still broken.
 */
import { isEnrichmentStage } from "@/lib/tina/spend-class";
import type { ErrorBucketsRow } from "@/types/tina";

import type { StageClass } from "./stage-health";

/**
 * - `new` — first seen inside the recent tail. The signature that matters most:
 *   an error type that did not exist last week is a regression, whatever its
 *   absolute count.
 * - `spiking` — older than the tail, but the latest day is well above its own
 *   historical daily average.
 * - `fading` — nothing in the recent tail; already over, kept for context.
 * - `steady` — present throughout at a flat rate. Usually the known-bad ones.
 */
export type ErrorTrend = "new" | "spiking" | "fading" | "steady";

export interface ErrorCluster {
  /** `${stage}::${errorType}` — stable React key. */
  key: string;
  stage: string;
  stageClass: StageClass;
  errorType: string;
  total: number;
  /** The part of `total` a retry fixed. Always 0 unless `includeRecovered`. */
  recoveredTotal: number;
  firstDay: string;
  lastDay: string;
  /** Distinct days this cluster appeared on. */
  days: number;
  /** Per-day counts, ascending — drives the inline sparkbar. */
  daily: Array<{ day: string; count: number }>;
  /** Count on the most recent day present in the whole payload (0 if absent). */
  latestCount: number;
  trend: ErrorTrend;
  /**
   * Distinct event ids, capped. `v_error_buckets.sample_event_ids` is an
   * unbounded `array_agg(DISTINCT event_id)` despite the name — a storm returns
   * every id, so never render the raw array.
   */
  sampleEventIds: string[];
  /** How many ids the payload held before the cap. */
  sampleTotal: number;
}

export interface ClusterOptions {
  /** Ids kept per cluster (default 5). */
  sampleLimit?: number;
  /** Days at the end of the window that count as "recent" (default 3). */
  recentDays?: number;
  /** Latest-day count must reach this before `spiking` can fire (default 5). */
  spikeFloor?: number;
  /** Latest day must exceed the prior daily mean by this factor (default 2). */
  spikeFactor?: number;
  /** Keep the buckets a later retry fixed (default false). */
  includeRecovered?: boolean;
}

/** How many errors in the payload a retry fixed — what the default view hides. */
export function countRecoveredErrors(rows: ErrorBucketsRow[]): number {
  return rows.reduce(
    (acc, r) => acc + (r.recovered ? (r.error_count ?? 0) : 0),
    0,
  );
}

const DAY_MS = 86_400_000;

function dayIndex(day: string): number {
  const parsed = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(parsed) ? parsed / DAY_MS : NaN;
}

interface Acc {
  stage: string;
  errorType: string;
  total: number;
  recoveredTotal: number;
  daily: Map<string, number>;
  ids: string[];
  idCount: number;
}

export function aggregateErrorClusters(
  rows: ErrorBucketsRow[],
  options: ClusterOptions = {},
): ErrorCluster[] {
  const {
    sampleLimit = 5,
    recentDays = 3,
    spikeFloor = 5,
    spikeFactor = 2,
    includeRecovered = false,
  } = options;

  const byKey = new Map<string, Acc>();
  let windowLastDay = "";

  for (const r of rows) {
    // The window's last day comes from every row, hidden ones too, so hiding
    // self-healed errors never shifts which days count as "recent".
    if (r.day > windowLastDay) windowLastDay = r.day;
    if (r.recovered && !includeRecovered) continue;
    const key = `${r.stage}::${r.error_type}`;
    let acc = byKey.get(key);
    if (!acc) {
      acc = {
        stage: r.stage,
        errorType: r.error_type,
        total: 0,
        recoveredTotal: 0,
        daily: new Map(),
        ids: [],
        idCount: 0,
      };
      byKey.set(key, acc);
    }
    acc.total += r.error_count ?? 0;
    if (r.recovered) acc.recoveredTotal += r.error_count ?? 0;
    acc.daily.set(r.day, (acc.daily.get(r.day) ?? 0) + (r.error_count ?? 0));
    const ids = r.sample_event_ids ?? [];
    acc.idCount += ids.length;
    for (const id of ids) {
      if (acc.ids.length >= sampleLimit) break;
      if (!acc.ids.includes(id)) acc.ids.push(id);
    }
  }

  const lastIndex = windowLastDay ? dayIndex(windowLastDay) : NaN;
  const recentCutoff = lastIndex - (recentDays - 1);

  const out: ErrorCluster[] = [];
  for (const [key, acc] of byKey) {
    const daily = Array.from(acc.daily, ([day, count]) => ({
      day,
      count,
    })).sort((a, b) => a.day.localeCompare(b.day));
    const firstDay = daily[0]!.day;
    const lastDay = daily[daily.length - 1]!.day;
    const latestCount = acc.daily.get(windowLastDay) ?? 0;

    let trend: ErrorTrend;
    if (dayIndex(firstDay) >= recentCutoff) {
      trend = "new";
    } else if (dayIndex(lastDay) < recentCutoff) {
      trend = "fading";
    } else {
      const priorDays = daily.filter((d) => d.day !== windowLastDay);
      const priorMean = priorDays.length
        ? priorDays.reduce((a, d) => a + d.count, 0) / priorDays.length
        : 0;
      trend =
        latestCount >= spikeFloor && latestCount > priorMean * spikeFactor
          ? "spiking"
          : "steady";
    }

    out.push({
      key,
      stage: acc.stage,
      stageClass: isEnrichmentStage(acc.stage) ? "enrichment" : "scrape",
      errorType: acc.errorType,
      total: acc.total,
      recoveredTotal: acc.recoveredTotal,
      firstDay,
      lastDay,
      days: daily.length,
      daily,
      latestCount,
      trend,
      sampleEventIds: acc.ids,
      sampleTotal: acc.idCount,
    });
  }

  // Regressions before volume: a brand-new error type with 12 occurrences is
  // more urgent than a known-bad one with 12,000.
  const rank: Record<ErrorTrend, number> = {
    new: 0,
    spiking: 1,
    steady: 2,
    fading: 3,
  };
  return out.sort(
    (a, b) =>
      rank[a.trend] - rank[b.trend] ||
      b.total - a.total ||
      a.key.localeCompare(b.key),
  );
}

/** Clusters worth an operator's attention right now. */
export function isRegression(cluster: ErrorCluster): boolean {
  return cluster.trend === "new" || cluster.trend === "spiking";
}
